import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Stack,
  AppBar,
  Toolbar,
  Typography,
  Dialog,
  IconButton,
  TableContainer,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Divider,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowDown,
  faMapLocationDot,
  faListOl,
} from "@fortawesome/free-solid-svg-icons";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
  Tooltip as EtiquetaMapa,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import * as XLSX from "xlsx/xlsx.mjs";

import { DataContext } from "../../../context/DataContext";
import { getRol, getToken, getUser } from "../../../api/Account";
import { getRutaCobroAsync } from "../../../api/Ruta";
import { simpleMessage } from "../../../helpers/Helpers";
import {
  AjustarVista,
  iconoInicio,
  iconoParada,
} from "../../../components/MapaSeguimiento";
import { CENTRO_POR_DEFECTO } from "../../../helpers/Seguimiento";
import NoData from "../../../components/NoData";
import { PrintReport } from "../../../components/PrintReport";
import { EncabezadoOrdenable, useOrdenTabla } from "../../../helpers/OrdenTabla";

const moneda = (valor) =>
  new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(
    valor
  );

const km = (valor) => `${valor.toFixed(2)} km`;

/**
 * Las mismas tres letras que el gestor ya lee en el cobro diario y en el teléfono, con el
 * mismo significado: del día, en mora, o el crédito entero vencido.
 */
const ESTADOS = {
  D: { etiqueta: "Al día", color: "#00a152" },
  M: { etiqueta: "Mora", color: "#ed6c02" },
  V: { etiqueta: "Vencido", color: "#d32f2f" },
};

const estadoDe = (tipo) => ESTADOS[tipo] ?? ESTADOS.D;

/** Par etiqueta/valor de los totales, igual que en el resto de los reportes. */
const Total = ({ titulo, valor, color, tamano }) => (
  <Stack textAlign="center">
    <Typography
      style={{ fontWeight: "bold", color: color ?? "#03a9f4", fontSize: tamano }}
    >
      {titulo}
    </Typography>
    <Typography style={{ fontSize: tamano }}>{valor}</Typography>
  </Stack>
);

const RutaCobro = () => {
  const { selectedGestor, selectedFecha } = useParams();
  const [ruta, setRuta] = useState(null);

  // Arranca en el mapa: es lo que responde de un vistazo por dónde queda cada cliente. La
  // lista es la que se imprime.
  const [vista, setVista] = useState("mapa");

  // La lista sale en el orden de la ruta, que es para lo que existe el reporte; desde ahí
  // se puede reordenar para leerla de otra forma sin que la ruta cambie.
  const TEXTOS = ["fullName", "address", "phone", "tipo"];
  const { orden, cambiarOrden, ordenar } = useOrdenTabla("orden", TEXTOS);
  const {
    orden: ordenSin,
    cambiarOrden: cambiarSin,
    ordenar: ordenarSin,
  } = useOrdenTabla("fullName", TEXTOS);

  const compRef = useRef();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getRutaCobroAsync(token, selectedGestor, selectedFecha);
      setIsLoading(false);

      if (!result.statusResponse) {
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setRuta(result.data.result);
    })();
  }, []);

  const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  @page { size: landscape;  margin: 4mm; }
`;

  if (ruta === null) {
    return null;
  }

  const paradas = ruta.paradas ?? [];
  const sinUbicacion = ruta.sinUbicacion ?? [];

  const trazo = paradas.map((p) => [p.latitude, p.longitude]);
  const origen =
    ruta.origenLatitude === null || ruta.origenLatitude === undefined
      ? null
      : [ruta.origenLatitude, ruta.origenLongitude];

  // El trazo del mapa arranca en el origen: sin ese primer tramo el recorrido parece
  // empezar en el primer cliente y no se ve de dónde salió el gestor.
  const trazoCompleto = origen === null ? trazo : [origen, ...trazo];

  const ahorroKm = ruta.totalKmSinOptimizar - ruta.totalKm;
  const ahorroPct =
    ruta.totalKmSinOptimizar > 0 ? (ahorroKm / ruta.totalKmSinOptimizar) * 100 : 0;

  // Los totales cuentan a TODOS los clientes del día, con ubicación o sin ella: el dinero
  // a cobrar no depende de si el cliente pudo dibujarse en el mapa.
  const todos = [...paradas, ...sinUbicacion];

  let totalCuota = 0;
  let totalAtraso = 0;
  let totalVencido = 0;
  let totalSaldo = 0;
  let contadorDia = 0;
  let contadorMora = 0;
  let contadorVenc = 0;

  // Mismo reparto que el cobro diario: la cuota la aportan los del día, el atraso los que
  // están en mora, y el vencido el saldo entero de los vencidos.
  todos.forEach((c) => {
    if (c.tipo === "D") {
      totalCuota += c.cuota;
      contadorDia += 1;
    } else if (c.tipo === "M") {
      contadorMora += 1;
      totalAtraso += c.atraso;
    } else {
      contadorVenc += 1;
      totalVencido += c.saldoTotal;
    }
    totalSaldo += c.saldoTotal;
  });

  const exportExcel = () => {
    let exp = [];

    todos.forEach((item) => {
      exp.push({
        orden: item.orden ?? "",
        cliente: item.fullName,
        direccion: item.address,
        telefono: item.phone,
        tipo: item.tipo,
        cuota: item.cuota,
        atraso: item.atraso,
        atrasoDias: item.atrasoDias,
        saldoTotal: item.saldoTotal,
        latitud: item.latitude ?? "",
        longitud: item.longitude ?? "",
        ubicacion: item.latitude === undefined ? "SIN UBICACION" : item.ubicacionFijada ? "FIJADA" : "APROXIMADA",
        tramoKm: item.distanciaDesdeAnteriorKm ?? "",
        acumuladoKm: item.acumuladoKm ?? "",
        gestor: ruta.gestorName,
      });
    });

    var bk = XLSX.utils.book_new(),
      bs = XLSX.utils.json_to_sheet(exp);

    XLSX.utils.book_append_sheet(bk, bs, "rutaCobro");
    XLSX.writeFile(bk, "RutaCobro.xlsx");
  };

  /**
   * La pantalla y la hoja impresa comparten el contenido pero no el tamaño de letra: en
   * papel hay que meter decenas de filas en una hoja —por eso el 9/8 que usan todos los
   * reportes—, y en pantalla no hay ninguna razón para achicar el texto.
   */
  const t = (compacto, chico, grande) => (compacto ? chico : grande);

  const parametros = (compacto) => (
    <Stack direction={"row"} justifyContent="space-between">
      <Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 11, 13)}
        >
          Parametros:
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Gestor Seleccionado: ${ruta.gestorName}`}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Fecha de Cobro: ${moment(ruta.fecha).format("L")}`}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Punto de partida: ${ruta.origenDescripcion}`}
        </Typography>
      </Stack>

      <Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 11, 13)}
        >
          Usuario:
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {user}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {rol}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Fecha y Hora: ${moment().format("DD/MM/YYYY hh:mm A")}`}
        </Typography>
      </Stack>
    </Stack>
  );

  const totales = (compacto) => {
    const tamano = t(compacto, 11, 14);

    return (
      <>
        <Stack direction="row" flex="row" justifyContent="space-around">
          <Total
            titulo="Créditos del Día"
            valor={contadorDia}
            color={ESTADOS.D.color}
            tamano={tamano}
          />
          <Total
            titulo="Total Cuotas Día"
            valor={moneda(totalCuota)}
            tamano={tamano}
          />
          <Total
            titulo="Créditos en Atraso"
            valor={contadorMora}
            color={ESTADOS.M.color}
            tamano={tamano}
          />
          <Total
            titulo="Total Atraso"
            valor={`${moneda(totalAtraso)} = ${
              totalSaldo > 0 ? ((totalAtraso / totalSaldo) * 100).toFixed(2) : "0.00"
            }%`}
            tamano={tamano}
          />
          <Total
            titulo="Créditos Vencidos"
            valor={contadorVenc}
            color={ESTADOS.V.color}
            tamano={tamano}
          />
          <Total
            titulo="Total Vencido"
            valor={moneda(totalVencido)}
            tamano={tamano}
          />
          <Total
            titulo="Total Saldo"
            valor={moneda(totalSaldo)}
            tamano={tamano}
          />
        </Stack>

        <Divider sx={{ marginY: "10px" }} />

        <Stack direction="row" flex="row" justifyContent="space-around">
          <Total
            titulo="Visitas en Ruta"
            valor={paradas.length}
            tamano={tamano}
          />
          <Total
            titulo="Recorrido"
            valor={km(ruta.totalKm)}
            tamano={tamano}
          />
          {/* El ahorro va contra el orden en que hoy sale la lista de cobro. Sin esa
          referencia, "18 km" no dice si la ruta sirvió de algo. */}
          <Total
            titulo="Ahorro sobre el Orden Actual"
            valor={
              ahorroKm > 0.01
                ? `${km(ahorroKm)} = ${ahorroPct.toFixed(2)}%`
                : "Ninguno"
            }
            tamano={tamano}
          />
          <Total
            titulo="Sin Ubicación"
            valor={sinUbicacion.length}
            color={isEmpty(sinUbicacion) ? undefined : ESTADOS.M.color}
            tamano={tamano}
          />
        </Stack>
      </>
    );
  };

  // La letra sola, igual que en el cobro diario: es la misma columna y tiene que leerse
  // igual en los dos reportes. Quien necesite el desglose lo tiene en los totales y en el
  // color de los marcadores del mapa.
  const celdaEstado = (fila, compacto) => (
    <TableCell align="center" scope="row" style={{ fontSize: t(compacto, 8, 13) }}>
      {fila.tipo}
    </TableCell>
  );

  const tabla = (compacto) => {
    const th = { fontSize: t(compacto, 9, 14) };
    const td = { fontSize: t(compacto, 8, 13) };

    return (
      <TableContainer>
        <Table aria-label="simple table" size="small">
          <TableHead>
            <TableRow>
              {/* Se puede reordenar, pero el "#" siempre dice el lugar que le toca en la
              ruta: reordenar la lista no cambia el recorrido, solo la forma de leerla.
              Tramo y Acumulado son de la ruta, así que solo se leen en secuencia. */}
              <EncabezadoOrdenable campo="orden" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                #
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="fullName" orden={orden} cambiarOrden={cambiarOrden} align="left" className="text-primary fw-bold" style={th}>
                Cliente
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="address" orden={orden} cambiarOrden={cambiarOrden} align="left" className="text-primary fw-bold" style={th}>
                Dirección
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="phone" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Teléfono
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="tipo" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Tipo
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="cuota" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Cuota
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="atraso" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Atraso
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="atrasoDias" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                A.Dias
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="saldoTotal" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Saldo Total
              </EncabezadoOrdenable>
              <TableCell align="center" className="text-primary fw-bold" style={th}>
                Tramo
              </TableCell>
              <TableCell align="center" className="text-primary fw-bold" style={th}>
                Acumulado
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordenar(paradas).map((row) => (
              <TableRow key={row.creditId}>
                <TableCell
                  align="center"
                  scope="row"
                  style={{ ...td, fontWeight: "bold" }}
                >
                  {row.orden}
                </TableCell>
                <TableCell align="left" component="th" scope="row" style={td}>
                  {row.fullName}
                  {/* Un punto deducido de un abono puede haber quedado donde se cobró de
                  paso y no donde vive; se marca para que se sepa cuál conviene corregir. */}
                  {row.ubicacionFijada ? null : (
                    <Typography
                      component="span"
                      style={{ fontSize: t(compacto, 7, 11) }}
                      color="text.secondary"
                    >
                      {" (ubicación aproximada)"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="left" component="th" scope="row" style={td}>
                  {row.address}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {row.phone}
                </TableCell>
                {celdaEstado(row, compacto)}
                <TableCell align="center" scope="row" style={td}>
                  {moneda(row.cuota)}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {moneda(row.atraso)}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {row.atrasoDias}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {moneda(row.saldoTotal)}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {km(row.distanciaDesdeAnteriorKm)}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {km(row.acumuladoKm)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const tablaSinUbicacion = (compacto) => {
    if (isEmpty(sinUbicacion)) {
      return null;
    }

    const th = { fontSize: t(compacto, 9, 14) };
    const td = { fontSize: t(compacto, 8, 13) };

    return (
      <>
        <Divider sx={{ marginY: "14px" }} />

        <Stack direction="row" spacing={1}>
          <Typography
            sx={{ color: "text.secondary", textAlign: "left" }}
            fontSize={t(compacto, 11, 14)}
          >
            Sin ubicación conocida:
          </Typography>
          <Typography
            sx={{ textAlign: "left", color: ESTADOS.M.color, fontWeight: "bold" }}
            fontSize={t(compacto, 11, 14)}
          >
            {`${sinUbicacion.length} créditos a visitar aparte`}
          </Typography>
        </Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 9, 13)}
        >
          Todavía no se les registró un cobro con ubicación. Al cobrarles con la aplicación
          quedan ubicados y entran en la ruta del próximo día.
        </Typography>
        <Divider />

        <TableContainer>
          <Table aria-label="simple table" size="small">
            <TableHead>
              <TableRow>
                <EncabezadoOrdenable campo="fullName" orden={ordenSin} cambiarOrden={cambiarSin} align="left" className="text-primary fw-bold" style={th}>
                  Cliente
                </EncabezadoOrdenable>
                <EncabezadoOrdenable campo="address" orden={ordenSin} cambiarOrden={cambiarSin} align="left" className="text-primary fw-bold" style={th}>
                  Dirección
                </EncabezadoOrdenable>
                <EncabezadoOrdenable campo="phone" orden={ordenSin} cambiarOrden={cambiarSin} align="center" className="text-primary fw-bold" style={th}>
                  Teléfono
                </EncabezadoOrdenable>
                <EncabezadoOrdenable campo="tipo" orden={ordenSin} cambiarOrden={cambiarSin} align="center" className="text-primary fw-bold" style={th}>
                  Tipo
                </EncabezadoOrdenable>
                <EncabezadoOrdenable campo="cuota" orden={ordenSin} cambiarOrden={cambiarSin} align="center" className="text-primary fw-bold" style={th}>
                  Cuota
                </EncabezadoOrdenable>
                <EncabezadoOrdenable campo="atraso" orden={ordenSin} cambiarOrden={cambiarSin} align="center" className="text-primary fw-bold" style={th}>
                  Atraso
                </EncabezadoOrdenable>
                <EncabezadoOrdenable campo="atrasoDias" orden={ordenSin} cambiarOrden={cambiarSin} align="center" className="text-primary fw-bold" style={th}>
                  A.Dias
                </EncabezadoOrdenable>
                <EncabezadoOrdenable campo="saldoTotal" orden={ordenSin} cambiarOrden={cambiarSin} align="center" className="text-primary fw-bold" style={th}>
                  Saldo Total
                </EncabezadoOrdenable>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordenarSin(sinUbicacion).map((row) => (
                <TableRow key={row.creditId}>
                  <TableCell align="left" component="th" scope="row" style={td}>
                    {row.fullName}
                  </TableCell>
                  <TableCell align="left" component="th" scope="row" style={td}>
                    {row.address}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {row.phone}
                  </TableCell>
                  {celdaEstado(row, compacto)}
                  <TableCell align="center" scope="row" style={td}>
                    {moneda(row.cuota)}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {moneda(row.atraso)}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {row.atrasoDias}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {moneda(row.saldoTotal)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <div>
      <Dialog fullScreen open={true}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
            <Typography
              sx={{ ml: 2, flex: 1, textAlign: "center" }}
              variant="h5"
              component="div"
            >
              {`${title} `}
            </Typography>
            <img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
          </Toolbar>
        </AppBar>

        <Stack display="flex" justifyContent="center">
          <Stack
            direction={"row"}
            justifyContent="space-between"
            paddingLeft={5}
            paddingRight={5}
            marginTop={1}
            marginBottom={1}
          >
            <IconButton
              onClick={() => exportExcel()}
              sx={{ color: "primary.main" }}
            >
              <FontAwesomeIcon
                icon={faCloudArrowDown}
                style={{ fontSize: 22, width: 22 }}
              />
            </IconButton>

            <Stack>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {slogan}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
                variant="h6"
                component="div"
              >
                Reporte de Ruta Óptima de Cobro
              </Typography>
            </Stack>

            <ReactToPrint
              trigger={() => {
                return (
                  <IconButton sx={{ color: "info.main" }}>
                    <PrintRoundedIcon style={{ fontSize: 22, width: 22 }} />
                  </IconButton>
                );
              }}
              content={() => compRef.current}
              pageStyle={pageStyle}
            />
          </Stack>

          <Box sx={{ paddingX: "22px", textAlign: "center" }}>
            {parametros(false)}
          </Box>
        </Stack>

        <Box sx={{ paddingX: "22px", textAlign: "center" }}>
          <Divider sx={{ marginY: "14px" }} />

          {isEmpty(todos) ? (
            <NoData />
          ) : (
            <>
              {totales(false)}
              <Divider sx={{ marginY: "14px" }} />

              {/* Mapa y lista son la misma ruta mirada de dos maneras: el mapa para ver por
              dónde queda cada cliente, la lista para leerla y llevarla impresa. Se eligen
              en vez de apilarse para que cada una use la pantalla completa. */}
              <Tabs
                value={vista}
                onChange={(e, valor) => setVista(valor)}
                centered
                sx={{ marginBottom: 2 }}
              >
                <Tab
                  value="mapa"
                  label="Vista en mapa"
                  icon={<FontAwesomeIcon icon={faMapLocationDot} />}
                  iconPosition="start"
                  sx={{ fontSize: 15, fontWeight: "bold" }}
                />
                <Tab
                  value="lista"
                  label="Vista en lista"
                  icon={<FontAwesomeIcon icon={faListOl} />}
                  iconPosition="start"
                  sx={{ fontSize: 15, fontWeight: "bold" }}
                />
              </Tabs>

              {isEmpty(paradas) ? (
                <Typography
                  fontSize={17}
                  color={ESTADOS.M.color}
                  marginY={3}
                  sx={{ maxWidth: 760, marginX: "auto", lineHeight: 1.5 }}
                >
                  Ninguno de los clientes de hoy tiene ubicación conocida todavía, así que
                  no hay ruta que dibujar ni ordenar. Se van ubicando solos a medida que se
                  les cobre desde la aplicación, y los tenés a todos listados más abajo.
                </Typography>
              ) : vista === "mapa" ? (
                <MapContainer
                  center={isEmpty(trazoCompleto) ? CENTRO_POR_DEFECTO : trazoCompleto[0]}
                  zoom={14}
                  style={{ height: "72vh", width: "100%", borderRadius: 15 }}
                  scrollWheelZoom
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  <AjustarVista posiciones={trazoCompleto} senal={paradas.length} />

                  {trazoCompleto.length > 1 ? (
                    <Polyline positions={trazoCompleto} color="#1769aa" weight={3} />
                  ) : null}

                  {origen === null ? null : (
                    <Marker position={origen} icon={iconoInicio()}>
                      <Popup>
                        <strong>Punto de partida</strong>
                        <br />
                        {ruta.origenDescripcion}
                      </Popup>
                    </Marker>
                  )}

                  {paradas.map((p) => (
                    <Marker
                      key={p.creditId}
                      position={[p.latitude, p.longitude]}
                      // El color dice el estado del cobro y no la calidad de la ubicación:
                      // en un mapa lo que hay que ver de un golpe es quién está vencido.
                      icon={iconoParada(p.orden, estadoDe(p.tipo).color)}
                    >
                      {/* El nombre va fijo sobre el marcador: el número solo dice el orden,
                      y para reconocer la ruta hay que ver de quién es cada punto sin ir
                      abriendo uno por uno. */}
                      <EtiquetaMapa permanent direction="right" offset={[14, -8]}>
                        {p.fullName.split(" ").slice(0, 2).join(" ")}
                      </EtiquetaMapa>
                      <Popup>
                        <strong style={{ fontSize: 14 }}>
                          {`${p.orden}. ${p.fullName}`}
                        </strong>
                        <br />
                        <strong style={{ color: estadoDe(p.tipo).color }}>
                          {estadoDe(p.tipo).etiqueta}
                          {p.atrasoDias > 0 ? ` · ${p.atrasoDias} días` : ""}
                        </strong>
                        <br />
                        {p.address}
                        <br />
                        {p.phone}
                        <br />
                        {`Cuota ${moneda(p.cuota)}`}
                        {p.atraso > 0 ? ` · Atraso ${moneda(p.atraso)}` : ""}
                        <br />
                        {`Saldo total ${moneda(p.saldoTotal)}`}
                        <br />
                        <small>
                          {p.ubicacionFijada
                            ? "Ubicación fijada"
                            : `Ubicación aproximada, del ${moment(p.ubicacionDe).format(
                                "L"
                              )}`}
                        </small>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              ) : (
                tabla(false)
              )}

              {/* Los que no tienen ubicación no se pueden dibujar, así que solo aparecen en
              la lista. En el mapa quedaría un hueco silencioso. */}
              {vista === "lista" || isEmpty(paradas) ? tablaSinUbicacion(false) : null}
            </>
          )}
        </Box>
      </Dialog>

      {/* La hoja impresa va sin mapa: en papel un mapa en tonos grises no orienta a nadie,
      y lo que el gestor lleva encima es la lista en orden. */}
      <div style={{ display: "none" }}>
        <PrintReport ref={compRef} titulo={"Reporte de Ruta Óptima de Cobro"}>
          <Container maxWidth={false} sx={{ textAlign: "center" }}>
            {parametros(true)}
            <Divider sx={{ marginY: "10px" }} />

            {totales(true)}
            <Divider sx={{ marginY: "10px" }} />

            {isEmpty(paradas) ? null : tabla(true)}
            {tablaSinUbicacion(true)}

            <Typography
              fontSize={8}
              textAlign="left"
              marginTop={1}
              color="text.secondary"
            >
              Las distancias son en línea recta, para ordenar las visitas. Los kilómetros
              reales de manejo son mayores.
            </Typography>
          </Container>
        </PrintReport>
      </div>
    </div>
  );
};

export default RutaCobro;
